import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Megaphone, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTime } from "@/lib/format";
import { markConsumed } from "@/lib/notificationRecovery";

/**
 * BroadcastModal
 * --------------
 * Native push notifications frequently truncate long announcements. When a
 * user taps a broadcast notification we deep-link them to
 * ``/?broadcast=<id>``. This component watches the URL for that query
 * parameter, fetches the full bilingual text from the backend, and shows the
 * entire announcement in an in-app modal so nothing gets cut off.
 *
 * It also listens for a `postMessage` from the service worker so that if a
 * notification is tapped while the PWA is already open, we can pop the modal
 * without a hard reload.
 */
export function BroadcastModal() {
  const { lang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [broadcast, setBroadcast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const openById = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError("");
    setBroadcast({ id }); // opens the modal immediately with a skeleton
    // Broadcast is being shown to the user — clear any pending recovery
    // record so we don't later say "you missed a broadcast" about content
    // they're actively viewing.
    markConsumed("broadcast");
    try {
      const { data } = await api.get(`/broadcasts/${id}`);
      setBroadcast(data);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 404
          ? (lang === "es" ? "Este aviso ya no esta disponible." : "This broadcast is no longer available.")
          : (lang === "es" ? "No se pudo cargar el aviso." : "Could not load the broadcast.")
      );
    } finally {
      setLoading(false);
    }
  }, [lang]);

  // Read the ?broadcast= param on every location change.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("broadcast");
    if (id && (!broadcast || broadcast.id !== id)) {
      openById(id);
    }
  }, [location.search, broadcast, openById]);

  // Listen for a postMessage from the service worker (notification tap while
  // the app is already open).
  useEffect(() => {
    const onMessage = (event) => {
      const msg = event?.data;
      if (msg && msg.type === "OPEN_BROADCAST" && msg.broadcast_id) {
        openById(msg.broadcast_id);
      }
    };
    if ("serviceWorker" in navigator && navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", onMessage);
      return () => navigator.serviceWorker.removeEventListener("message", onMessage);
    }
    return undefined;
  }, [openById]);

  const handleClose = () => {
    // Remove ?broadcast=<id> and the SW-added ?nt=1 notification-tap
    // marker from the URL without losing the current path.
    const params = new URLSearchParams(location.search);
    let mutated = false;
    if (params.has("broadcast")) { params.delete("broadcast"); mutated = true; }
    if (params.has("nt")) { params.delete("nt"); mutated = true; }
    if (mutated) {
      const qs = params.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : "" }, { replace: true });
    }
    setBroadcast(null);
    setError("");
  };

  const open = !!broadcast;
  const title = broadcast
    ? (lang === "es"
        ? (broadcast.title_es || broadcast.title_en || "")
        : (broadcast.title_en || broadcast.title_es || ""))
    : "";
  const body = broadcast
    ? (lang === "es"
        ? (broadcast.body_es || broadcast.body_en || "")
        : (broadcast.body_en || broadcast.body_es || ""))
    : "";
  const usedFallback = broadcast && (
    lang === "es"
      ? (!broadcast.body_es && !!broadcast.body_en)
      : (!broadcast.body_en && !!broadcast.body_es)
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="max-w-lg w-[94vw] max-h-[86vh] overflow-y-auto p-0 gap-0 rounded-2xl"
        data-testid="broadcast-modal"
      >
        {broadcast && (
          <div className="flex flex-col">
            <div className="flex items-start gap-3 p-5 border-b border-border/60">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Megaphone className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {lang === "es" ? "Aviso de la iglesia" : "Church broadcast"}
                </p>
                {loading && !title ? (
                  <Skeleton className="h-6 w-3/4 mt-1" />
                ) : (
                  <h2
                    className="font-serif-display text-xl font-semibold leading-tight break-words"
                    data-testid="broadcast-modal-title"
                  >
                    {title || (lang === "es" ? "Aviso" : "Announcement")}
                  </h2>
                )}
                {broadcast.created_at && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {fmtDateTime(broadcast.created_at, lang)}
                  </p>
                )}
              </div>
            </div>

            <div className="p-5 space-y-3">
              {error ? (
                <p className="text-sm text-destructive" data-testid="broadcast-modal-error">
                  {error}
                </p>
              ) : loading && !body ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-11/12" />
                  <Skeleton className="h-4 w-10/12" />
                </div>
              ) : (
                <p
                  className="whitespace-pre-line leading-relaxed text-foreground/90 text-sm"
                  data-testid="broadcast-modal-body"
                >
                  {body || (lang === "es" ? "(Sin contenido)" : "(No content)")}
                </p>
              )}

              {usedFallback && (
                <p className="text-[11px] text-muted-foreground">
                  {lang === "es"
                    ? "Este aviso solo esta disponible en Ingles."
                    : "This broadcast is only available in Spanish."}
                </p>
              )}
            </div>

            <div className="p-4 pt-0 flex justify-end">
              <Button variant="outline" onClick={handleClose} data-testid="broadcast-modal-close">
                <X className="mr-1 h-4 w-4" />
                {lang === "es" ? "Cerrar" : "Close"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
